// (2026-05-23 v3) Orquestador del visor publico de documentos via QR.
// Splash de marca (Sirius + Quantus) + vista HTML responsive nativa
// para INV (factura), DG (guia de despacho), RETIVA y RETISLR
// (retenciones). El iframe-con-PDF anterior se reemplaza por componentes
// HTML porque el viewer nativo de PDF NO renderiza en navegadores
// moviles.
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { decryptText } from "../controllers/api";
import api from "../services/axiosConfig";
import PublicSplash from "./PublicSplash";
import PublicInvoiceView from "./PublicInvoiceView";
import PublicDispatchGuideView from "./PublicDispatchGuideView";
import PublicWithholdingView from "./PublicWithholdingView";
import { getPublicConfig } from "../controllers/api_config";

// (2026-05-23) Default por si el backend tarda en responder /api/config/public.
// El valor REAL viene de configuracion.clave='splash_duration_ms' y es
// editable sin redeploy.
const SPLASH_MIN_MS_DEFAULT = 3000;
const FETCH_HARD_TIMEOUT_MS = 12000;

// Mapeo de tipo de documento -> endpoint del backend.
const ENDPOINT_BY_TYPE = {
  INV:      "/api/documents/factura/",
  DG:       "/api/documents/guia-despacho/",
  RETIVA:   "/api/documents/retencion-iva/",
  RETISLR:  "/api/documents/retencion-islr/",
};

const PublicDocument = () => {
  const { type, id } = useParams();
  const [phase, setPhase] = useState("splash"); // splash | content | error
  const [data, setData] = useState(null);
  const [realId, setRealId] = useState(null);
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    let mounted = true;
    let timeoutId = null;
    let finalized = false;          // (2026-05-23 fix) idempotencia
    const start = Date.now();
    let splashMinMs = SPLASH_MIN_MS_DEFAULT;

    // Fetch de configuracion publica en paralelo (sin bloquear). Cuando llegue
    // ajusta la duracion minima del splash. Si nunca llega (offline/timeout)
    // se usa el default.
    getPublicConfig().then((cfg) => {
      if (!mounted || !cfg) return;
      const raw = cfg.splash_duration_ms;
      const parsed = parseInt(raw, 10);
      if (!Number.isNaN(parsed) && parsed >= 500 && parsed <= 10000) {
        splashMinMs = parsed;
      }
    }).catch(() => {});

    const finalize = (next, errorText) => {
      // (2026-05-23 fix) idempotente: la 1a llamada gana. Si el contenido
      // cargo bien y luego el hard timeout dispara, este guard evita que
      // pise la vista del documento con la pantalla de error.
      if (!mounted || finalized) return;
      finalized = true;
      // Cancelar el hard timeout para no fire-after-success.
      if (timeoutId) { clearTimeout(timeoutId); timeoutId = null; }
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, splashMinMs - elapsed);
      if (errorText) setErrMsg(errorText);
      setTimeout(() => { if (mounted) setPhase(next); }, remaining);
    };

    timeoutId = setTimeout(() => {
      // Solo cuenta si nada lo finalizo antes. `finalized` se evalua aqui
      // (no en captura), porque finalize lo flipea por referencia de
      // closure compartido.
      finalize("error", "El documento esta tardando demasiado en cargar. Intenta nuevamente o verifica tu conexion.");
    }, FETCH_HARD_TIMEOUT_MS);

    (async () => {
      try {
        if (!id) throw new Error("Identificador del documento faltante.");
        // (2026-05-23) Acepta TANTO el token cifrado (QR escaneado) como el
        // qr_token UUID en crudo (link "Ver factura en linea" del correo).
        // Si decryptText falla o devuelve algo demasiado corto, asume que
        // `id` ya es el qr_token UUID y lo usa directo. _buscar_factura en
        // el backend acepta ambos (UUID o ID numerico).
        let decoded = null;
        try { decoded = decryptText(id); } catch (_) { decoded = null; }
        const looksValid = typeof decoded === "string" && decoded.length >= 10;
        const effective = looksValid ? decoded : id;
        if (mounted) setRealId(effective);

        const path = ENDPOINT_BY_TYPE[type];
        if (!path) throw new Error("Tipo de documento desconocido: " + (type || ""));

        const res = await api.get(`${path}${effective}`);
        if (!mounted) return;
        const d = res?.data;
        if (!d) throw new Error("El documento no devolvio datos.");
        setData(d);
        finalize("content");
      } catch (e) {
        finalize("error", e?.message || "Error cargando el documento.");
      }
    })();

    return () => {
      mounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [id, type]);

  // Splash: siempre primero (minimo ~3 s, ver useEffect).
  if (phase === "splash") {
    return <PublicSplash />;
  }

  if (phase === "error") {
    return (
      <div style={{
        position: "fixed", inset: 0, background: "#0a0e1a", color: "#cbd5e1",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: 24, textAlign: "center",
        fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
        <h2 style={{ color: "#f87171", margin: "0 0 12px 0", fontSize: 20 }}>
          No pudimos cargar el documento
        </h2>
        <p style={{ maxWidth: 480, lineHeight: 1.5, margin: 0 }}>{errMsg}</p>
        <p style={{ color: "#64748b", fontSize: 13, marginTop: 22 }}>
          Verifica que el QR sea válido o intenta nuevamente.
        </p>
      </div>
    );
  }

  // phase === "content"
  const wrapStyle = { animation: "qi-content-in 350ms ease-out" };
  const fadeKeyframes = `@keyframes qi-content-in { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:none; } }`;

  let body = null;
  if (type === "INV") {
    body = <PublicInvoiceView data={data} />;
  } else if (type === "DG") {
    body = <PublicDispatchGuideView data={data} realId={realId} />;
  } else if (type === "RETIVA" || type === "RETISLR") {
    body = <PublicWithholdingView data={data} type={type} realId={realId} />;
  } else {
    body = (
      <div style={{ padding: 40, textAlign: "center" }}>
        Tipo de documento no soportado: {String(type)}
      </div>
    );
  }

  return (
    <div style={wrapStyle}>
      {body}
      <style>{fadeKeyframes}</style>
    </div>
  );
};

export default PublicDocument;
